import { Component, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { ThemeService } from './services/theme.service';
import { BusyService } from './services/busy.service';
import { REFERRAL_CODE_STORAGE_KEY, saveReferralCode } from './shared/referral-code.util';
import { captureMarketingReferral } from './shared/marketing-referral.util';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrl: './app.component.scss'
})
export class AppComponent {

  private titleService = inject(Title);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private theme = inject(ThemeService);
  busyService = inject(BusyService);

  // Referral code sharing (ICIP-SS-R-005): a shared link like
  // https://stage-portal-eu.firstorion.com/?code=WELCOME10 lands here first. Capture the code
  // BEFORE Angular routing strips the query string, and stash it for the checkout page to
  // pre-fill and auto-apply. sessionStorage (not local) so it is scoped to this browser session.
  // Key + shape rules live in the shared referral-code util (single source of truth).
  static readonly REFERRAL_CODE_KEY = REFERRAL_CODE_STORAGE_KEY;


  constructor() {
    this.captureReferralCode();
    captureMarketingReferral();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((data) => {
        if (data['title']) {
          this.titleService.setTitle(`${data['title']} | EURO Portal`);
        }
      });
  }

  toggle() {
    this.theme.toggleTheme();
  }

  /**
   * Reads a referral code from the landing URL's query string (?promo=...) and stores it for the
   * checkout page. Normalised to upper-case; ignored if absent/blank. Only the value is kept — the
   * URL is left untouched (Angular's router will drop the query param on its own navigation).
   *
   * <p>Uses {@code promo} (not {@code code}) deliberately: the OIDC/Cognito login flow returns to
   * the app with {@code ?code=<auth_code>}, and reusing that name caused the auth code to overwrite
   * the referral code. {@code promo} cannot collide with the OAuth callback.
   */
  private captureReferralCode(): void {
    try {
      const promo = new URLSearchParams(window.location.search).get('promo');
      if (promo && promo.trim()) {
        saveReferralCode(promo);
      }
    } catch {
      // no-op — non-browser context or blocked storage; referral simply won't pre-fill.
    }
  }
}
